import { RotateCcw, ArrowLeft } from "lucide-react";
import Fireworks from "./Fireworks";
import { TOTAL_LEVELS } from "../lib/levels";

/**
 * Level-summary modals — game over + win overlays.
 *
 * Extracted from `GameCanvas.jsx` on 2026-05-13. Both pieces only need
 * primitive props (score, level, navigation callbacks) so they live
 * cleanly outside the canvas closure.
 *
 *   <GameOverOverlay />   — replaces the inline {gameOver && (...)} block.
 *   <WinOverlay />        — replaces the inline {won && (...)} block, including
 *                            the auto-warp pill for non-final campaign levels.
 *
 * The shared `<Overlay>` component is imported from the same UI module
 * GameCanvas already uses, so theming stays in lockstep.
 */
import { Overlay } from "./ui/GameHud";

export function GameOverOverlay({
  endless,
  score,
  wave,
  onRetry,
  onExit,
}) {
  return (
    <Overlay>
      <div className="font-heading font-black uppercase text-5xl tracking-tighter text-[#ff007f] mb-3">
        {endless ? "Run Over" : "Game Over"}
      </div>
      <div className="font-mono-cosmic text-sm tracking-widest text-white/70 mb-2">
        FINAL SCORE · {Number(score).toLocaleString()}
      </div>
      {endless && (
        <div
          className="font-mono-cosmic text-xs tracking-[0.3em] uppercase text-[#00ffff] mb-6"
          data-testid="endless-final-wave"
        >
          Survived to wave {String(wave || 1).padStart(2, "0")}
        </div>
      )}
      <div className="flex gap-3">
        <button
          className="btn-neon"
          onClick={onRetry}
          data-testid="retry-btn"
        >
          <span className="inline-flex items-center gap-2">
            <RotateCcw className="w-4 h-4" /> {endless ? "Play Again" : "Retry"}
          </span>
        </button>
        <button
          className="btn-neon cyan"
          onClick={onExit}
          data-testid="exit-gameover-btn"
        >
          <span className="inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {endless ? "Exit" : "Levels"}
          </span>
        </button>
      </div>
    </Overlay>
  );
}

export function WinOverlay({
  level,
  score,
  endless,
  isDaily,
  mode,
  onAdvance,
  onNextSector,
  onExit,
}) {
  // Auto-advance path — non-final, non-daily, non-endless campaign level
  // with an onAdvance callback. The parent navigates to the next level after
  // a 750ms timer; we render only a subtle warping pill instead of the legacy
  // giant "Sector Cleared" overlay.
  const autoWarp =
    onAdvance &&
    !endless &&
    !isDaily &&
    mode === "campaign" &&
    level < TOTAL_LEVELS;

  return (
    <>
      <Fireworks />
      {autoWarp ? (
        <div
          className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none"
          data-testid="warp-transition"
          style={{ animation: "fadeIn 0.25s ease-out" }}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#00ffff]/70 bg-black/70 backdrop-blur-sm font-mono-cosmic text-[10px] tracking-[0.45em] uppercase text-[#00ffff]"
            style={{ boxShadow: "0 0 30px rgba(0,255,255,0.45)" }}
          >
            <span className="inline-block w-1.5 h-1.5 bg-[#00ffff] rounded-full animate-pulse" />
            Warping · Level {level + 1}
          </div>
        </div>
      ) : (
        <Overlay>
          <div className="font-heading font-black uppercase text-5xl tracking-tighter text-[#00ff41] mb-2">
            {level >= TOTAL_LEVELS ? "Galaxy Cleared" : "Sector Cleared"}
          </div>
          <div className="font-mono-cosmic text-sm tracking-widest text-white/70 mb-1">
            SCORE · {Number(score).toLocaleString()}
          </div>
          {level < TOTAL_LEVELS && !isDaily && (
            <div className="font-mono-cosmic text-xs tracking-widest text-[#00ffff] mb-8">
              NEXT SECTOR UNLOCKED
            </div>
          )}
          <div className="flex gap-3">
            {level < TOTAL_LEVELS && !isDaily && (
              <button
                className="btn-neon"
                onClick={onNextSector}
                data-testid="next-level-btn"
              >
                Next Sector
              </button>
            )}
            <button
              className="btn-neon cyan"
              onClick={onExit}
              data-testid="exit-win-btn"
            >
              Levels
            </button>
          </div>
        </Overlay>
      )}
    </>
  );
}
